'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Poppins } from 'next/font/google';
import { useZodForm, requestDemoSchema, scheduleDemoSchema, type RequestDemoFormData, type ScheduleDemoFormData } from '@/shared/validation';
import { SubmitHandler } from 'react-hook-form';
import { BRAND_BLUE, BRAND_GREEN, BRAND_YELLOW } from '@voley/shared-types/constants';

const poppins = Poppins({ subsets: ['latin'], weight: ['700'] });

// Brand colors
const primaryColor = BRAND_BLUE;
const accentGreen = BRAND_GREEN;
const accentYellow = BRAND_YELLOW;

export default function RequestDemoPage() {
  // Main form with validation
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useZodForm(requestDemoSchema, {
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      phone: '',
      teamSize: undefined,
      message: '',
      agm: '',
      crm: '',
    }
  });

  // Schedule form with validation
  const {
    register: registerSchedule,
    handleSubmit: handleScheduleSubmit,
    formState: { errors: scheduleErrors },
    watch: watchSchedule,
  } = useZodForm(scheduleDemoSchema, {
    defaultValues: {
      date: '',
      time: '',
    }
  });

  const scheduleValues = watchSchedule();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Generate available dates (M-F only) for the next 2 weeks
  const generateAvailableDates = () => {
    const dates: { value: string; label: string; }[] = [];
    const today = new Date();

    for (let i = 1; i <= 14; i++) {
      const date = new Date();
      date.setDate(today.getDate() + i);

      if (date.getDay() > 0 && date.getDay() < 6) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        });
      }
    }

    return dates;
  };

  // Generate available time slots from 8am to 6pm
  const generateTimeSlots = () => {
    const slots: { value: string; label: string; }[] = [];

    for (let hour = 8; hour <= 17; hour++) {
      const hourLabel = hour > 12 ? (hour - 12) : hour;
      const amPm = hour >= 12 ? 'PM' : 'AM';

      slots.push({
        value: `${hour}:00`,
        label: `${hourLabel}:00 ${amPm}`
      });

      slots.push({
        value: `${hour}:30`,
        label: `${hourLabel}:30 ${amPm}`
      });
    }

    return slots;
  };

  const onSubmit: SubmitHandler<RequestDemoFormData> = async (data) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/public/external-requests/demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit demo request');
      }

      const result = await response.json();

      if (result.data?.id) {
        setRequestId(result.data.id);
      }

      setIsSubmitted(true);
      setIsScheduling(true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onScheduleSubmit: SubmitHandler<ScheduleDemoFormData> = async (data) => {
    setIsSubmitting(true);

    try {
      if (!requestId) {
        throw new Error('No request ID found');
      }

      const selectedDate = new Date(data.date);
      const formattedDate = selectedDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      const hour = parseInt(data.time.split(':')[0]);
      const minutes = data.time.split(':')[1];
      const amPm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour > 12 ? hour - 12 : hour;
      const formattedTime = `${hour12}:${minutes} ${amPm}`;

      const response = await fetch(`/api/public/external-requests/demo/${requestId}/schedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scheduledDate: data.date,
          scheduledTime: data.time,
          formattedDateTime: `${formattedDate} at ${formattedTime}`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to schedule demo');
      }

      setIsScheduling(false);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Mobile: compact hero + form first. Desktop: full hero + sidebar layout */}

      {/* Desktop Hero — hidden on mobile */}
      <div className="hidden lg:block bg-white pt-6 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className={`text-7xl lg:text-8xl xl:text-9xl ${poppins.className} tracking-tight leading-tight`}>
              <span className="block font-medium text-gray-900">See Voley in action.</span>
              <span className="block font-medium text-gray-900">Schedule your demo.</span>
            </h1>
            <p className="mt-4 max-w-3xl mx-auto text-xl lg:text-2xl font-light text-gray-600">
              Meet directly with our team for a personalized walkthrough tailored to your agency&apos;s needs.
            </p>
            <div className="mt-4 flex items-center justify-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: accentGreen }} />
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: accentYellow }} />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Form Section with sidebar — hidden on mobile */}
      <div className="hidden lg:block py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-12">
            {/* What to Expect — desktop sidebar */}
            <div className="col-span-1">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">What to expect</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mr-3" style={{ backgroundColor: `${primaryColor}15` }}>
                    <svg className="h-4 w-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Direct conversation with a founder</h3>
                    <p className="text-gray-600 text-sm mt-1">No sales reps—talk to someone who built the product</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mr-3" style={{ backgroundColor: `${accentGreen}25` }}>
                    <svg className="h-4 w-4 text-[#5aaa7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Live walkthrough tailored to you</h3>
                    <p className="text-gray-600 text-sm mt-1">See how Voley fits your specific workflows</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mr-3" style={{ backgroundColor: `${accentYellow}30` }}>
                    <svg className="h-4 w-4 text-[#c9a820]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Honest discussion about fit</h3>
                    <p className="text-gray-600 text-sm mt-1">We&apos;ll tell you if Voley is right for your agency</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mr-3" style={{ backgroundColor: `${primaryColor}15` }}>
                    <svg className="h-4 w-4" style={{ color: primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900">30 minutes</h3>
                    <p className="text-gray-600 text-sm mt-1">Quick, focused, and respectful of your time</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Form */}
            <div className="col-span-2">
              <div
                className="rounded-3xl p-2"
                style={{ background: `linear-gradient(135deg, ${primaryColor}30 0%, ${accentGreen}30 50%, ${accentYellow}30 100%)` }}
              >
                <div className="bg-white rounded-2xl p-8">
                  {!isSubmitted ? (
                    <>
                      <h2 className="text-xl font-semibold text-gray-900 mb-6">Tell us about your agency</h2>

                      <form onSubmit={handleSubmit(onSubmit as (data: unknown) => Promise<void>) as (e: React.FormEvent) => void}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                          <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                              First name
                            </label>
                            <input
                              type="text"
                              id="firstName"
                              autoComplete="given-name"
                              {...register('firstName')}
                              className={`shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm rounded-lg p-2.5 border ${
                                errors.firstName ? 'border-red-300' : 'border-gray-300'
                              }`}
                            />
                            {errors.firstName && (
                              <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>
                            )}
                          </div>
                          <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                              Last name
                            </label>
                            <input
                              type="text"
                              id="lastName"
                              autoComplete="family-name"
                              {...register('lastName')}
                              className={`shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm rounded-lg p-2.5 border ${
                                errors.lastName ? 'border-red-300' : 'border-gray-300'
                              }`}
                            />
                            {errors.lastName && (
                              <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                              Work email
                            </label>
                            <input
                              id="email"
                              type="email"
                              autoComplete="email"
                              {...register('email')}
                              className={`shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm rounded-lg p-2.5 border ${
                                errors.email ? 'border-red-300' : 'border-gray-300'
                              }`}
                            />
                            {errors.email && (
                              <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                            )}
                          </div>
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                              Phone number
                            </label>
                            <input
                              type="tel"
                              id="phone"
                              autoComplete="tel"
                              placeholder="555-123-4567"
                              {...register('phone')}
                              className={`shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm rounded-lg p-2.5 border ${
                                errors.phone ? 'border-red-300' : 'border-gray-300'
                              }`}
                            />
                            {errors.phone && (
                              <p className="mt-1 text-xs text-red-600">{errors.phone.message}</p>
                            )}
                          </div>
                        </div>

                        <div className="mb-6">
                          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                            Agency / Company name
                          </label>
                          <input
                            type="text"
                            id="company"
                            autoComplete="organization"
                            {...register('company')}
                            className={`shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm rounded-lg p-2.5 border ${
                              errors.company ? 'border-red-300' : 'border-gray-300'
                            }`}
                          />
                          {errors.company && (
                            <p className="mt-1 text-xs text-red-600">{errors.company.message}</p>
                          )}
                        </div>

                        <div className="mb-6">
                          <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 mb-1">
                            Team size
                          </label>
                          <select
                            id="teamSize"
                            {...register('teamSize')}
                            className={`shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm rounded-lg p-2.5 border ${
                              errors.teamSize ? 'border-red-300' : 'border-gray-300'
                            }`}
                          >
                            <option value="">Select team size</option>
                            <option value="1-5">1-5 employees</option>
                            <option value="6-10">6-10 employees</option>
                            <option value="11-25">11-25 employees</option>
                            <option value="26-50">26-50 employees</option>
                            <option value="51+">51+ employees</option>
                          </select>
                          {errors.teamSize && (
                            <p className="mt-1 text-xs text-red-600">{errors.teamSize.message}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                          <div>
                            <label htmlFor="agm" className="block text-sm font-medium text-gray-700 mb-1">
                              Agency Management System
                            </label>
                            <input
                              type="text"
                              id="agm"
                              placeholder="e.g., HawkSoft, Applied Epic"
                              {...register('agm')}
                              className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                            />
                          </div>
                          <div>
                            <label htmlFor="crm" className="block text-sm font-medium text-gray-700 mb-1">
                              CRM <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                              type="text"
                              id="crm"
                              placeholder="e.g., Salesforce, HubSpot"
                              {...register('crm')}
                              className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                            />
                          </div>
                        </div>

                        <div className="mb-6">
                          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                            Anything specific you&apos;d like to see? <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <textarea
                            id="message"
                            rows={3}
                            {...register('message')}
                            className="shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-lg p-2.5 border"
                            placeholder="Tell us about your workflow or specific features you're interested in..."
                          />
                        </div>

                        {submitError && (
                          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3">
                            <p className="text-sm text-red-700">{submitError}</p>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full inline-flex justify-center items-center px-4 py-3 border-0 text-base font-medium rounded-full shadow-sm text-white hover:shadow-lg transition-all hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {isSubmitting ? 'Submitting...' : 'Continue to schedule'}
                        </button>
                      </form>
                    </>
                  ) : isScheduling ? (
                    <>
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">Choose a date and time</h2>
                      <p className="text-gray-600 mb-6">Select a time that works for your demo session.</p>

                      <form onSubmit={handleScheduleSubmit(onScheduleSubmit as (data: unknown) => Promise<void>) as (e: React.FormEvent) => void}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                          <div>
                            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                              Date
                            </label>
                            <select
                              id="date"
                              {...registerSchedule('date')}
                              className={`shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm rounded-lg p-2.5 border ${
                                scheduleErrors.date ? 'border-red-300' : 'border-gray-300'
                              }`}
                            >
                              <option value="">Select a date</option>
                              {generateAvailableDates().map((date) => (
                                <option key={date.value} value={date.value}>
                                  {date.label}
                                </option>
                              ))}
                            </select>
                            {scheduleErrors.date && (
                              <p className="mt-1 text-xs text-red-600">{scheduleErrors.date.message}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                              Time <span className="text-gray-400 font-normal">(Mountain Time)</span>
                            </label>
                            <select
                              id="time"
                              {...registerSchedule('time')}
                              className={`shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm rounded-lg p-2.5 border ${
                                scheduleErrors.time ? 'border-red-300' : 'border-gray-300'
                              }`}
                            >
                              <option value="">Select a time</option>
                              {generateTimeSlots().map((time) => (
                                <option key={time.value} value={time.value}>
                                  {time.label}
                                </option>
                              ))}
                            </select>
                            {scheduleErrors.time && (
                              <p className="mt-1 text-xs text-red-600">{scheduleErrors.time.message}</p>
                            )}
                          </div>
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full inline-flex justify-center items-center px-4 py-3 border-0 text-base font-medium rounded-full shadow-sm text-white hover:shadow-lg transition-all hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {isSubmitting ? 'Scheduling...' : 'Confirm demo'}
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6" style={{ backgroundColor: `${accentGreen}20` }}>
                        <svg className="h-8 w-8 text-[#5aaa7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-semibold text-gray-900 mb-2">You&apos;re all set!</h3>
                      <p className="text-gray-600 mb-2">
                        Your demo is scheduled for{' '}
                        <span className="font-semibold">
                          {scheduleValues.date ? new Date(scheduleValues.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : ''}
                        </span>
                        {scheduleValues.time && (
                          <span className="font-semibold">
                            {' '}at {parseInt(scheduleValues.time.split(':')[0]) > 12 ? (parseInt(scheduleValues.time.split(':')[0]) - 12) : scheduleValues.time.split(':')[0]}:{scheduleValues.time.split(':')[1]} {parseInt(scheduleValues.time.split(':')[0]) >= 12 ? 'PM' : 'AM'} MT
                          </span>
                        )}
                      </p>
                      <p className="text-gray-500 text-sm mb-8">
                        We&apos;ve sent the details to your email. Looking forward to meeting you!
                      </p>
                      <Link
                        href="/"
                        className="inline-flex items-center justify-center px-6 py-3 border-0 text-base font-medium rounded-full text-white hover:shadow-lg transition-all hover:scale-[1.01]"
                        style={{ backgroundColor: primaryColor }}
                      >
                        Return to home
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Mobile Layout ─── shown only below lg breakpoint */}
      <div className="lg:hidden bg-gray-50 py-6 sm:py-8">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          {/* Compact mobile hero */}
          <div className="text-center mb-5">
            <h1 className={`text-3xl sm:text-4xl ${poppins.className} tracking-tight leading-tight`}>
              <span className="block font-medium text-gray-900">See Voley in action.</span>
            </h1>
            <p className="mt-2 text-sm sm:text-base font-light text-gray-600">
              30-min personalized demo with a founder. No sales reps.
            </p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: primaryColor }} />
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accentGreen }} />
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: accentYellow }} />
            </div>
          </div>

          {/* Mobile form card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              {!isSubmitted ? (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Book your demo</h2>

                  <form onSubmit={handleSubmit(onSubmit as (data: unknown) => Promise<void>) as (e: React.FormEvent) => void} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="mobile-firstName" className="block text-sm font-medium text-gray-700 mb-1">
                          First name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="mobile-firstName"
                          autoComplete="given-name"
                          {...register('firstName')}
                          className={`block w-full text-sm rounded-lg p-2.5 border ${errors.firstName ? 'border-red-300' : 'border-gray-300'} focus:ring-brand focus:border-brand`}
                        />
                        {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>}
                      </div>
                      <div>
                        <label htmlFor="mobile-lastName" className="block text-sm font-medium text-gray-700 mb-1">
                          Last name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="mobile-lastName"
                          autoComplete="family-name"
                          {...register('lastName')}
                          className={`block w-full text-sm rounded-lg p-2.5 border ${errors.lastName ? 'border-red-300' : 'border-gray-300'} focus:ring-brand focus:border-brand`}
                        />
                        {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="mobile-email" className="block text-sm font-medium text-gray-700 mb-1">
                        Work email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="mobile-email"
                        autoComplete="email"
                        {...register('email')}
                        className={`block w-full text-sm rounded-lg p-2.5 border ${errors.email ? 'border-red-300' : 'border-gray-300'} focus:ring-brand focus:border-brand`}
                      />
                      {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="mobile-phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="mobile-phone"
                        autoComplete="tel"
                        placeholder="555-123-4567"
                        {...register('phone')}
                        className={`block w-full text-sm rounded-lg p-2.5 border ${errors.phone ? 'border-red-300' : 'border-gray-300'} focus:ring-brand focus:border-brand`}
                      />
                    </div>

                    <div>
                      <label htmlFor="mobile-company" className="block text-sm font-medium text-gray-700 mb-1">
                        Agency name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="mobile-company"
                        autoComplete="organization"
                        {...register('company')}
                        className={`block w-full text-sm rounded-lg p-2.5 border ${errors.company ? 'border-red-300' : 'border-gray-300'} focus:ring-brand focus:border-brand`}
                      />
                      {errors.company && <p className="mt-1 text-xs text-red-600">{errors.company.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="mobile-teamSize" className="block text-sm font-medium text-gray-700 mb-1">
                        Team size <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="mobile-teamSize"
                        {...register('teamSize')}
                        className={`block w-full text-sm rounded-lg p-2.5 border ${errors.teamSize ? 'border-red-300' : 'border-gray-300'} focus:ring-brand focus:border-brand`}
                      >
                        <option value="">Select...</option>
                        <option value="1-5">1-5</option>
                        <option value="6-10">6-10</option>
                        <option value="11-25">11-25</option>
                        <option value="26-50">26-50</option>
                        <option value="51+">51+</option>
                      </select>
                      {errors.teamSize && <p className="mt-1 text-xs text-red-600">{errors.teamSize.message}</p>}
                    </div>

                    <div>
                      <label htmlFor="mobile-agm" className="block text-sm font-medium text-gray-700 mb-1">
                        AMS <span className="text-gray-400 font-normal">(e.g. HawkSoft)</span>
                      </label>
                      <input
                        type="text"
                        id="mobile-agm"
                        placeholder="HawkSoft, Applied Epic, etc."
                        {...register('agm')}
                        className="block w-full text-sm rounded-lg p-2.5 border border-gray-300 focus:ring-brand focus:border-brand"
                      />
                    </div>

                    {submitError && (
                      <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                        <p className="text-sm text-red-700">{submitError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 border-0 text-base font-medium rounded-full text-white hover:shadow-lg transition-all hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Continue to schedule'}
                    </button>
                  </form>
                </>
              ) : isScheduling ? (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Pick a time</h2>
                  <p className="text-sm text-gray-600 mb-4">Select a date and time for your demo.</p>

                  <form onSubmit={handleScheduleSubmit(onScheduleSubmit as (data: unknown) => Promise<void>) as (e: React.FormEvent) => void} className="space-y-4">
                    <div>
                      <label htmlFor="mobile-date" className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                      <select
                        id="mobile-date"
                        {...registerSchedule('date')}
                        className={`block w-full text-sm rounded-lg p-2.5 border ${scheduleErrors.date ? 'border-red-300' : 'border-gray-300'} focus:ring-brand focus:border-brand`}
                      >
                        <option value="">Select a date</option>
                        {generateAvailableDates().map((date) => (
                          <option key={date.value} value={date.value}>{date.label}</option>
                        ))}
                      </select>
                      {scheduleErrors.date && <p className="mt-1 text-xs text-red-600">{scheduleErrors.date.message}</p>}
                    </div>
                    <div>
                      <label htmlFor="mobile-time" className="block text-sm font-medium text-gray-700 mb-1">Time <span className="text-gray-400 font-normal">(MT)</span></label>
                      <select
                        id="mobile-time"
                        {...registerSchedule('time')}
                        className={`block w-full text-sm rounded-lg p-2.5 border ${scheduleErrors.time ? 'border-red-300' : 'border-gray-300'} focus:ring-brand focus:border-brand`}
                      >
                        <option value="">Select a time</option>
                        {generateTimeSlots().map((time) => (
                          <option key={time.value} value={time.value}>{time.label}</option>
                        ))}
                      </select>
                      {scheduleErrors.time && <p className="mt-1 text-xs text-red-600">{scheduleErrors.time.message}</p>}
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 border-0 text-base font-medium rounded-full text-white hover:shadow-lg transition-all hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {isSubmitting ? 'Scheduling...' : 'Confirm demo'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full mb-4" style={{ backgroundColor: `${accentGreen}20` }}>
                    <svg className="h-7 w-7 text-[#5aaa7a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">You&apos;re all set!</h3>
                  <p className="text-gray-600 text-sm mb-2">
                    Your demo is scheduled for{' '}
                    <span className="font-semibold">
                      {scheduleValues.date ? new Date(scheduleValues.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : ''}
                    </span>
                    {scheduleValues.time && (
                      <span className="font-semibold">
                        {' '}at {parseInt(scheduleValues.time.split(':')[0]) > 12 ? (parseInt(scheduleValues.time.split(':')[0]) - 12) : scheduleValues.time.split(':')[0]}:{scheduleValues.time.split(':')[1]} {parseInt(scheduleValues.time.split(':')[0]) >= 12 ? 'PM' : 'AM'} MT
                      </span>
                    )}
                  </p>
                  <p className="text-gray-500 text-xs mb-6">We&apos;ve sent the details to your email.</p>
                  <Link
                    href="/"
                    className="inline-flex items-center justify-center px-6 py-2.5 border-0 text-sm font-medium rounded-full text-white hover:shadow-lg transition-all"
                    style={{ backgroundColor: primaryColor }}
                  >
                    Return to home
                  </Link>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* CTA Section */}
      <div style={{ background: `linear-gradient(135deg, ${primaryColor}cc 0%, ${accentGreen}cc 50%, ${accentYellow}cc 100%)` }}>
        <div className="max-w-7xl mx-auto py-10 sm:py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white">
            <span className="block">Have questions first?</span>
            <span className="block opacity-90 font-medium">We&apos;re happy to chat anytime.</span>
          </h2>
          <div className="mt-6 sm:mt-8 flex lg:mt-0 lg:shrink-0">
            <div className="inline-flex w-full sm:w-auto">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-gray-900 bg-white hover:bg-slate-50 transition-all hover:scale-[1.02] hover:shadow-lg w-full sm:w-auto"
              >
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
